// src/lib/locations.ts: shared country / city lists for location fields.
//
// The list is deliberately a superset of what any one form previously offered:
// CreateFund carried its own inline COUNTRIES/CITIES, and merging rather than
// replacing keeps every option that existing fund records may already hold.

export const COUNTRIES: string[] = [
  'Australia',
  'Austria',
  'Bahrain',
  'Belgium',
  'Brazil',
  'British Virgin Islands',
  'Canada',
  'Cayman Islands',
  'China',
  'Cyprus',
  'Denmark',
  'Dubai (UAE)',
  'Finland',
  'France',
  'Germany',
  'Guernsey',
  'Hong Kong',
  'India',
  'Ireland',
  'Israel',
  'Italy',
  'Japan',
  'Jersey',
  'Kuwait',
  'Luxembourg',
  'Malaysia',
  'Malta',
  'Netherlands',
  'New Zealand',
  'Norway',
  'Qatar',
  'Saudi Arabia',
  'Singapore',
  'South Africa',
  'South Korea',
  'Spain',
  'Sweden',
  'Switzerland',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'Other',
];

export const CITIES_BY_COUNTRY: Record<string, string[]> = {
  'United Kingdom': ['London', 'Edinburgh', 'Manchester', 'Birmingham', 'Bristol'],
  'United States':  ['New York', 'San Francisco', 'Boston', 'Chicago', 'Los Angeles', 'Miami', 'Dallas', 'Houston', 'Atlanta', 'Greenwich'],
  'Switzerland':    ['Zurich', 'Geneva', 'Basel', 'Zug'],
  'Germany':        ['Frankfurt', 'Munich', 'Hamburg', 'Berlin'],
  'France':         ['Paris', 'Lyon'],
  'Hong Kong':      ['Hong Kong'],
  'Singapore':      ['Singapore'],
  'Dubai (UAE)':    ['Dubai'],
  // Listed in the brief's city map; kept so the key resolves if it is ever
  // offered as a country. 'United Arab Emirates' below is the reachable entry.
  'Abu Dhabi (UAE)': ['Abu Dhabi'],
  'United Arab Emirates': ['Dubai', 'Abu Dhabi', 'Sharjah'],
  'Luxembourg':     ['Luxembourg City'],
  'Ireland':        ['Dublin'],
  'Netherlands':    ['Amsterdam', 'Rotterdam'],
  'Sweden':         ['Stockholm', 'Gothenburg'],
  'Norway':         ['Oslo'],
  'Denmark':        ['Copenhagen'],
  'Australia':      ['Sydney', 'Melbourne'],
  'Japan':          ['Tokyo', 'Osaka'],
  'Canada':         ['Toronto', 'Vancouver', 'Montreal'],
  'Cayman Islands': ['George Town', 'Grand Cayman'],
  'Guernsey':       ['St Peter Port'],
  'Jersey':         ['St Helier'],
  'British Virgin Islands': ['Road Town'],
};

export function getCities(country: string): string[] {
  return CITIES_BY_COUNTRY[country] || [];
}

// Split a stored "City, Country" string (firms.headquarters is a single
// column) back into its parts. Anything that does not parse cleanly is treated
// as the city so no text is silently dropped.
export function splitHeadquarters(hq: string | null | undefined): { country: string; city: string } {
  const raw = (hq || '').trim();
  if (!raw) return { country: '', city: '' };
  const parts = raw.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length === 0) return { country: '', city: '' };
  const last = parts[parts.length - 1];
  if (COUNTRIES.includes(last)) {
    return { country: last, city: parts.slice(0, -1).join(', ') };
  }
  return { country: '', city: raw };
}

export function joinHeadquarters(country: string, city: string): string {
  return [city, country].filter(Boolean).join(', ');
}
